import { useState, type ReactNode } from "react";
import type { ContentType } from "../types";
import { ASSESSMENT_ROLE_LABELS } from "../types";
import { DragList } from "./DragList";
import { Upload } from "./Upload";
import { inputClass, labelClass, secondaryButtonClass, textareaClass } from "./ui";

type Content = Record<string, unknown>;

type FieldProps = { value: Content; onChange: (next: Content) => void };

interface Props {
  type: ContentType;
  value: Content;
  onChange: (next: Content) => void;
}

function set(value: Content, patch: Content): Content {
  return { ...value, ...patch };
}

function arr<T>(value: Content, key: string): T[] {
  return Array.isArray(value[key]) ? (value[key] as T[]) : [];
}

// ContentTypeFields renders the structured, type-specific admin form for
// every content type — no JSON is ever shown or edited by hand.
export function ContentTypeFields({ type, value, onChange }: Props) {
  switch (type) {
    case "video":
      return <VideoFields value={value} onChange={onChange} />;
    case "audio":
      return <AudioFields value={value} onChange={onChange} />;
    case "audio_slides":
      return <AudioSlidesFields value={value} onChange={onChange} />;
    case "text":
      return <TextFields value={value} onChange={onChange} />;
    case "image":
    case "gallery":
      return <ImageFields value={value} onChange={onChange} multiple={type === "gallery"} />;
    case "slides":
      return <SlidesFields value={value} onChange={onChange} />;
    case "downloadable":
      return <DownloadFields value={value} onChange={onChange} />;
    case "resource_pack":
      return <ResourcePackFields value={value} onChange={onChange} />;
    case "external_link":
      return <LinkFields value={value} onChange={onChange} />;
    case "embed":
      return <EmbedFields value={value} onChange={onChange} />;
    case "practical_activity":
      return <PracticalActivityFields value={value} onChange={onChange} />;
    case "reflection":
      return <ReflectionFields value={value} onChange={onChange} />;
    case "quiz":
      return <QuizFields value={value} onChange={onChange} />;
    case "form":
      return <FormFields value={value} onChange={onChange} />;
    default:
      return null;
  }
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={labelClass}>
      {label}
      {children}
    </label>
  );
}

function isHTTPURL(value: string): boolean {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}

// An MP4 already in storage (e.g. a public bucket link) can be used as-is
// instead of uploading it again. Teachers play and download it like an upload.
function VideoLinkField({ onUse }: { onUse: (url: string) => void }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  function use() {
    const url = draft.trim();
    if (!isHTTPURL(url)) {
      setError("Enter a full http(s) link to an MP4 file.");
      return;
    }
    setError("");
    setDraft("");
    onUse(url);
  }

  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium text-content-primary">Or paste a video link</span>
      <div className="flex gap-2">
        <input
          className={inputClass}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              use();
            }
          }}
          placeholder="https://storage.googleapis.com/…/video.mp4"
        />
        <button type="button" className={secondaryButtonClass} onClick={use} disabled={!draft.trim()}>
          Use link
        </button>
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}

function VideoFields({ value, onChange }: FieldProps) {
  const source = (value.source as string) || "uploaded";
  return (
    <div className="grid gap-4">
      <Field label="Source">
        <select
          className={inputClass}
          value={source}
          onChange={(e) => onChange(set(value, { source: e.target.value, video_url: "" }))}
        >
          <option value="uploaded">Uploaded MP4</option>
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
        </select>
      </Field>
      {source === "uploaded" ? (
        <>
          <Upload
            purpose="video"
            label="Video file"
            value={value.video_url as string}
            onUploaded={(url) => onChange(set(value, { video_url: url }))}
            onRemove={() => onChange(set(value, { video_url: "" }))}
          />
          {!value.video_url && (
            <VideoLinkField onUse={(url) => onChange(set(value, { video_url: url }))} />
          )}
        </>
      ) : (
        <Field label={`${source === "youtube" ? "YouTube" : "Vimeo"} URL`}>
          <input
            className={inputClass}
            value={(value.video_url as string) || ""}
            onChange={(e) => onChange(set(value, { video_url: e.target.value }))}
            placeholder={source === "youtube" ? "https://www.youtube.com/watch?v=…" : "https://vimeo.com/…"}
          />
        </Field>
      )}
      <Field label="Caption">
        <input className={inputClass} value={(value.caption as string) || ""} onChange={(e) => onChange(set(value, { caption: e.target.value }))} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Upload
          purpose="subtitle"
          label="Subtitle file (optional)"
          value={value.subtitle_url as string}
          onUploaded={(url) => onChange(set(value, { subtitle_url: url }))}
          onRemove={() => onChange(set(value, { subtitle_url: "" }))}
        />
        <Upload
          purpose="thumbnail"
          label="Poster / thumbnail (optional)"
          value={value.poster_url as string}
          onUploaded={(url) => onChange(set(value, { poster_url: url }))}
          onRemove={() => onChange(set(value, { poster_url: "" }))}
        />
      </div>
      <Field label="Transcript URL (optional)">
        <input className={inputClass} value={(value.transcript_url as string) || ""} onChange={(e) => onChange(set(value, { transcript_url: e.target.value }))} />
      </Field>
    </div>
  );
}

function AudioFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Upload
        purpose="audio"
        label="Audio file"
        value={value.audio_url as string}
        onUploaded={(url) => onChange(set(value, { audio_url: url }))}
        onRemove={() => onChange(set(value, { audio_url: "" }))}
      />
      <Upload
        purpose="image"
        label="Cover image (optional)"
        value={value.cover_image_url as string}
        onUploaded={(url) => onChange(set(value, { cover_image_url: url }))}
        onRemove={() => onChange(set(value, { cover_image_url: "" }))}
      />
      <Field label="Caption">
        <input className={inputClass} value={(value.caption as string) || ""} onChange={(e) => onChange(set(value, { caption: e.target.value }))} />
      </Field>
      <Field label="Transcript URL (optional)">
        <input className={inputClass} value={(value.transcript_url as string) || ""} onChange={(e) => onChange(set(value, { transcript_url: e.target.value }))} />
      </Field>
    </div>
  );
}

function AudioSlidesFields({ value, onChange }: FieldProps) {
  const slides = arr<Record<string, unknown>>(value, "slides");
  return (
    <div className="grid gap-4">
      <Upload
        purpose="audio"
        label="Audio file"
        value={value.audio_url as string}
        onUploaded={(url) => onChange(set(value, { audio_url: url }))}
        onRemove={() => onChange(set(value, { audio_url: "" }))}
      />
      <Field label="Caption">
        <input className={inputClass} value={(value.caption as string) || ""} onChange={(e) => onChange(set(value, { caption: e.target.value }))} />
      </Field>
      <div>
        <p className="mb-2 text-sm font-medium text-content-primary">Slides, in order, each with a timing point</p>
        <DragList
          items={slides}
          onChange={(next) => onChange(set(value, { slides: reindex(next) }))}
          onRemove={(i) => onChange(set(value, { slides: reindex(slides.filter((_, idx) => idx !== i)) }))}
          addLabel="Add slide"
          onAdd={() => onChange(set(value, { slides: [...slides, { image_url: "", title: "", timestamp_seconds: 0, position: slides.length + 1 }] }))}
          renderItem={(slide, i) => (
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Upload
                purpose="image"
                label={`Slide ${i + 1} image`}
                value={slide.image_url as string}
                onUploaded={(url) => updateAt(slides, i, { image_url: url }, (next) => onChange(set(value, { slides: next })))}
              />
              <label className={labelClass}>
                Timing (seconds)
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={(slide.timestamp_seconds as number) ?? 0}
                  onChange={(e) => updateAt(slides, i, { timestamp_seconds: Number(e.target.value) }, (next) => onChange(set(value, { slides: next })))}
                />
              </label>
              <label className={`${labelClass} sm:col-span-2`}>
                Slide title
                <input
                  className={inputClass}
                  value={(slide.title as string) || ""}
                  onChange={(e) => updateAt(slides, i, { title: e.target.value }, (next) => onChange(set(value, { slides: next })))}
                />
              </label>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function TextFields({ value, onChange }: FieldProps) {
  return (
    <Field label="Body (rich text is sanitized on save)">
      <textarea
        className={`${textareaClass} min-h-48`}
        value={(value.body as string) || ""}
        onChange={(e) => onChange(set(value, { body: e.target.value }))}
      />
    </Field>
  );
}

function ImageFields({ value, onChange, multiple }: FieldProps & { multiple: boolean }) {
  const images = arr<Record<string, unknown>>(value, "images");
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-content-primary">
        {multiple ? "Images, in gallery order" : "Image"}
      </p>
      <DragList
        items={images}
        onChange={(next) => onChange(set(value, { images: reindex(next) }))}
        onRemove={(i) => onChange(set(value, { images: reindex(images.filter((_, idx) => idx !== i)) }))}
        addLabel={multiple ? "Add image" : "Set image"}
        onAdd={() => {
          if (!multiple && images.length >= 1) return;
          onChange(set(value, { images: [...images, { url: "", caption: "", alt_text: "", position: images.length + 1 }] }));
        }}
        renderItem={(img, i) => (
          <div className="grid gap-3">
            <Upload
              purpose="image"
              label={`Image ${i + 1}`}
              value={img.url as string}
              onUploaded={(url) => updateAt(images, i, { url }, (next) => onChange(set(value, { images: next })))}
            />
            <label className={labelClass}>
              Caption
              <input
                className={inputClass}
                value={(img.caption as string) || ""}
                onChange={(e) => updateAt(images, i, { caption: e.target.value }, (next) => onChange(set(value, { images: next })))}
              />
            </label>
            <label className={labelClass}>
              Alt text (accessibility)
              <input
                className={inputClass}
                value={(img.alt_text as string) || ""}
                onChange={(e) => updateAt(images, i, { alt_text: e.target.value }, (next) => onChange(set(value, { images: next })))}
              />
            </label>
          </div>
        )}
      />
    </div>
  );
}

function SlidesFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Upload
        purpose="slides"
        label="Slide deck (PDF, PPT, or PPTX)"
        value={value.file_url as string}
        fileName={value.title as string}
        onUploaded={(url, name) => {
          const ext = name.split(".").pop()?.toLowerCase();
          const fileType = ext === "pdf" ? "pdf" : ext === "ppt" ? "ppt" : "pptx";
          onChange(set(value, { file_url: url, file_type: fileType }));
        }}
        onRemove={() => onChange(set(value, { file_url: "", file_type: "" }))}
      />
      <Field label="Title">
        <input className={inputClass} value={(value.title as string) || ""} onChange={(e) => onChange(set(value, { title: e.target.value }))} />
      </Field>
      <Field label="Description">
        <textarea className={textareaClass} value={(value.description as string) || ""} onChange={(e) => onChange(set(value, { description: e.target.value }))} />
      </Field>
    </div>
  );
}

function DownloadFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Upload
        purpose="document"
        label="File"
        value={value.file_url as string}
        fileName={value.file_name as string}
        onUploaded={(url, name, size, mime) => onChange(set(value, { file_url: url, file_name: name, file_size: size, mime_type: mime }))}
        onRemove={() => onChange(set(value, { file_url: "", file_name: "" }))}
      />
      <Field label="Display name">
        <input className={inputClass} value={(value.file_name as string) || ""} onChange={(e) => onChange(set(value, { file_name: e.target.value }))} />
      </Field>
      <Field label="Description">
        <textarea className={textareaClass} value={(value.description as string) || ""} onChange={(e) => onChange(set(value, { description: e.target.value }))} />
      </Field>
    </div>
  );
}

function ResourcePackFields({ value, onChange }: FieldProps) {
  const files = arr<Record<string, unknown>>(value, "files");
  return (
    <div className="grid gap-4">
      <Field label="Pack title">
        <input className={inputClass} value={(value.title as string) || ""} onChange={(e) => onChange(set(value, { title: e.target.value }))} />
      </Field>
      <Field label="Description">
        <textarea className={textareaClass} value={(value.description as string) || ""} onChange={(e) => onChange(set(value, { description: e.target.value }))} />
      </Field>
      <div>
        <p className="mb-2 text-sm font-medium text-content-primary">Files in this pack</p>
        <DragList
          items={files}
          onChange={(next) => onChange(set(value, { files: reindex(next) }))}
          onRemove={(i) => onChange(set(value, { files: reindex(files.filter((_, idx) => idx !== i)) }))}
          addLabel="Add file"
          onAdd={() => onChange(set(value, { files: [...files, { file_url: "", file_name: "", description: "", position: files.length + 1 }] }))}
          renderItem={(file, i) => (
            <div className="grid gap-3">
              <Upload
                purpose="resource"
                label={`File ${i + 1}`}
                value={file.file_url as string}
                fileName={file.file_name as string}
                onUploaded={(url, name, size, mime) =>
                  updateAt(files, i, { file_url: url, file_name: name, file_size: size, mime_type: mime }, (next) => onChange(set(value, { files: next })))
                }
              />
              <label className={labelClass}>
                Description
                <input
                  className={inputClass}
                  value={(file.description as string) || ""}
                  onChange={(e) => updateAt(files, i, { description: e.target.value }, (next) => onChange(set(value, { files: next })))}
                />
              </label>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function LinkFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Field label="URL">
        <input className={inputClass} value={(value.url as string) || ""} onChange={(e) => onChange(set(value, { url: e.target.value }))} placeholder="https://…" />
      </Field>
      <Field label="Link label">
        <input className={inputClass} value={(value.label as string) || ""} onChange={(e) => onChange(set(value, { label: e.target.value }))} />
      </Field>
      <Field label="Description">
        <textarea className={textareaClass} value={(value.description as string) || ""} onChange={(e) => onChange(set(value, { description: e.target.value }))} />
      </Field>
      <Field label="Open in">
        <select className={inputClass} value={(value.open_in as string) || "new"} onChange={(e) => onChange(set(value, { open_in: e.target.value }))}>
          <option value="new">New tab</option>
          <option value="current">Current tab</option>
        </select>
      </Field>
      <Upload
        purpose="thumbnail"
        label="Thumbnail (optional)"
        value={value.thumbnail_url as string}
        onUploaded={(url) => onChange(set(value, { thumbnail_url: url }))}
        onRemove={() => onChange(set(value, { thumbnail_url: "" }))}
      />
    </div>
  );
}

const EMBED_PROVIDERS = ["youtube", "vimeo", "google_slides", "google_forms", "google_drive", "other"];

function EmbedFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Field label="Provider">
        <select className={inputClass} value={(value.provider as string) || "other"} onChange={(e) => onChange(set(value, { provider: e.target.value }))}>
          {EMBED_PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p.replace("_", " ")}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Embed URL">
        <input className={inputClass} value={(value.embed_url as string) || ""} onChange={(e) => onChange(set(value, { embed_url: e.target.value }))} placeholder="https://…" />
      </Field>
      <Field label="Title">
        <input className={inputClass} value={(value.title as string) || ""} onChange={(e) => onChange(set(value, { title: e.target.value }))} />
      </Field>
      <Field label="Aspect ratio">
        <select className={inputClass} value={(value.aspect_ratio as string) || "16:9"} onChange={(e) => onChange(set(value, { aspect_ratio: e.target.value }))}>
          <option value="16:9">16:9</option>
          <option value="4:3">4:3</option>
          <option value="1:1">1:1</option>
        </select>
      </Field>
      <p className="text-xs text-content-secondary">Embeds render in a sandboxed frame; only the providers above are allowed.</p>
    </div>
  );
}

const RESPONSE_TYPES = ["none", "confirmation", "short_text", "long_text", "file_upload"];

function PracticalActivityFields({ value, onChange }: FieldProps) {
  const files = arr<Record<string, unknown>>(value, "files");
  return (
    <div className="grid gap-4">
      <Field label="Instructions">
        <textarea className={`${textareaClass} min-h-32`} value={(value.instructions as string) || ""} onChange={(e) => onChange(set(value, { instructions: e.target.value }))} />
      </Field>
      <Field label="Expected action">
        <input className={inputClass} value={(value.expected_action as string) || ""} onChange={(e) => onChange(set(value, { expected_action: e.target.value }))} />
      </Field>
      <Field label="Learner response type">
        <select className={inputClass} value={(value.response_type as string) || "none"} onChange={(e) => onChange(set(value, { response_type: e.target.value }))}>
          {RESPONSE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace("_", " ")}
            </option>
          ))}
        </select>
      </Field>
      <div>
        <p className="mb-2 text-sm font-medium text-content-primary">Supporting files</p>
        <DragList
          items={files}
          onChange={(next) => onChange(set(value, { files: reindex(next) }))}
          onRemove={(i) => onChange(set(value, { files: reindex(files.filter((_, idx) => idx !== i)) }))}
          addLabel="Add file"
          onAdd={() => onChange(set(value, { files: [...files, { file_url: "", file_name: "", position: files.length + 1 }] }))}
          renderItem={(file, i) => (
            <Upload
              purpose="resource"
              label={`File ${i + 1}`}
              value={file.file_url as string}
              fileName={file.file_name as string}
              onUploaded={(url, name) => updateAt(files, i, { file_url: url, file_name: name }, (next) => onChange(set(value, { files: next })))}
            />
          )}
        />
      </div>
    </div>
  );
}

function ReflectionFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Field label="Prompt">
        <textarea className={`${textareaClass} min-h-24`} value={(value.prompt as string) || ""} onChange={(e) => onChange(set(value, { prompt: e.target.value }))} />
      </Field>
      <Field label="Response type">
        <select className={inputClass} value={(value.response_type as string) || "short_text"} onChange={(e) => onChange(set(value, { response_type: e.target.value }))}>
          <option value="short_text">Short text</option>
          <option value="long_text">Long text</option>
        </select>
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Min length (optional)">
          <input type="number" min={0} className={inputClass} value={(value.min_length as number) ?? ""} onChange={(e) => onChange(set(value, { min_length: e.target.value ? Number(e.target.value) : null }))} />
        </Field>
        <Field label="Max length (optional)">
          <input type="number" min={0} className={inputClass} value={(value.max_length as number) ?? ""} onChange={(e) => onChange(set(value, { max_length: e.target.value ? Number(e.target.value) : null }))} />
        </Field>
      </div>
      <Field label="Visibility">
        <select className={inputClass} value={(value.visibility as string) || "private"} onChange={(e) => onChange(set(value, { visibility: e.target.value }))}>
          <option value="private">Private to learner</option>
          <option value="facilitator_visible">Visible to facilitator</option>
        </select>
      </Field>
    </div>
  );
}

function QuizFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Pass score (%)">
          <input type="number" min={0} max={100} className={inputClass} value={(value.pass_mark as number) ?? ""} onChange={(e) => onChange(set(value, { pass_mark: e.target.value ? Number(e.target.value) : null }))} />
        </Field>
        <Field label="Role in the study">
          <select className={inputClass} value={(value.assessment_role as string) || ""} onChange={(e) => onChange(set(value, { assessment_role: e.target.value || null }))}>
            <option value="">Not tagged yet</option>
            {Object.entries(ASSESSMENT_ROLE_LABELS).map(([role, label]) => (
              <option key={role} value={role}>{label}</option>
            ))}
          </select>
        </Field>
      </div>
      <p className="text-xs text-content-secondary">
        Tag pre- and post-tests so results can be compared. Changing the pass score or the two options below, or editing a question, starts a new edition{typeof value.version === "number" ? ` (this is edition ${value.version})` : ""}; each attempt records the edition it was taken on.
      </p>
      <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
        <input type="checkbox" className="h-4 w-4 accent-interactive" checked={Boolean(value.show_feedback)} onChange={(e) => onChange(set(value, { show_feedback: e.target.checked }))} />
        Show correct answers and reasons to teachers after submission
      </label>
      <label className="flex items-center gap-2 text-sm font-medium text-content-primary">
        <input type="checkbox" className="h-4 w-4 accent-interactive" checked={Boolean(value.shuffle_questions)} onChange={(e) => onChange(set(value, { shuffle_questions: e.target.checked }))} />
        Shuffle question order per attempt
      </label>
      <p className="text-xs text-content-secondary">Save this item first, then use the question builder to add questions.</p>
    </div>
  );
}

function FormFields({ value, onChange }: FieldProps) {
  return (
    <div className="grid gap-4">
      <Field label="Form title">
        <input className={inputClass} value={(value.title as string) || ""} onChange={(e) => onChange(set(value, { title: e.target.value }))} />
      </Field>
      <p className="text-xs text-content-secondary">Save this item first, then use the form builder to add fields.</p>
    </div>
  );
}

function reindex(items: Record<string, unknown>[]): Record<string, unknown>[] {
  return items.map((item, index) => ({ ...item, position: index + 1 }));
}

function updateAt(
  items: Record<string, unknown>[],
  index: number,
  patch: Record<string, unknown>,
  apply: (next: Record<string, unknown>[]) => void
) {
  const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
  apply(next);
}

export function defaultContentFor(type: ContentType): Content {
  switch (type) {
    case "video":
      return { video_url: "", source: "uploaded" };
    case "audio":
      return { audio_url: "" };
    case "audio_slides":
      return { audio_url: "", slides: [] };
    case "text":
      return { body: "" };
    case "image":
    case "gallery":
      return { images: [] };
    case "slides":
      return { file_url: "", file_type: "pdf", title: "" };
    case "downloadable":
      return { file_url: "", file_name: "" };
    case "resource_pack":
      return { title: "", files: [] };
    case "external_link":
      return { url: "", label: "", open_in: "new" };
    case "embed":
      return { embed_url: "", provider: "other", aspect_ratio: "16:9" };
    case "practical_activity":
      return { instructions: "", response_type: "none", files: [] };
    case "reflection":
      return { prompt: "", response_type: "short_text", visibility: "private" };
    case "quiz":
      return { attempts: 1, show_feedback: true, shuffle_questions: false };
    case "form":
      return { title: "", settings: {} };
    default:
      return {};
  }
}
